import trendsService, { type WeeklyReportDay, type WeeklyReportMeal } from "../services/TrendsService";
import { getLocalDateString } from "./homePageUtils";
import { MEDICAL_DISCLAIMER_TEXT } from "./medicalDisclaimer";

const BRAND = { r: 115, g: 125, b: 254 };
const HIGH_SPIKE_MGDL = 80;

type PdfLine = {
  text: string;
  size: number;
  color?: "ink" | "muted" | "white";
  bold?: boolean;
  section?: boolean;
};

function shiftLocalDate(dateStr: string, deltaDays: number): string {
  const date = new Date(`${dateStr}T12:00:00`);
  date.setDate(date.getDate() + deltaDays);
  return getLocalDateString(date);
}

function formatRange(start: string, end: string): string {
  const fmt = (value: string) =>
    new Date(`${value}T12:00:00`).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  return `${fmt(start)} - ${fmt(end)}`;
}

function isHighSpike(meal: WeeklyReportMeal): boolean {
  const category = (meal.spike_category ?? "").toLowerCase();
  if (category.includes("high")) return true;
  return typeof meal.spike_mg_dl === "number" && meal.spike_mg_dl >= HIGH_SPIKE_MGDL;
}

function foodLabel(meal: WeeklyReportMeal): string {
  const foods = (meal.food_names ?? []).map((name) => name.trim()).filter(Boolean);
  if (foods.length > 0) return foods.join(", ");
  return meal.meal_name?.trim() || "Meal";
}

function readingText(reading: Record<string, unknown>): string {
  const pick = (...keys: string[]) => {
    for (const key of keys) {
      const value = reading[key];
      if (typeof value === "string" && value.trim()) return value.trim();
      if (typeof value === "number" && Number.isFinite(value)) return String(value);
    }
    return "";
  };
  const label = (pick("reading_type", "type", "label", "name") || "Reading").replace(/_/g, " ");
  const value = pick("value", "mg_dl", "reading", "glucose_mg_dl", "hba1c");
  const unit = pick("unit") || (value ? "mg/dL" : "");
  const when = pick("time", "entered_at", "logged_at", "date");
  return [label, value ? `${value}${unit ? ` ${unit}` : ""}` : "", when].filter(Boolean).join(" · ");
}

function reportLines(days: WeeklyReportDay[], start: string, end: string): PdfLine[] {
  const scored = days.filter((day) => typeof day.bite_balance_score === "number");
  const avgScore = scored.length
    ? Math.round(scored.reduce((sum, day) => sum + (day.bite_balance_score ?? 0), 0) / scored.length)
    : null;
  const avgSteps = days.length
    ? Math.round(days.reduce((sum, day) => sum + (day.steps || 0), 0) / days.length)
    : 0;

  const meals = days.flatMap((day) =>
    (day.meals ?? []).map((meal) => ({ day, meal })),
  );
  const highSpikes = meals
    .filter(({ meal }) => isHighSpike(meal))
    .sort((a, b) => (b.meal.spike_mg_dl ?? 0) - (a.meal.spike_mg_dl ?? 0));
  const readings = days.flatMap((day) =>
    (day.blood_sugar_readings ?? []).map((reading) => ({
      date: day.date,
      text: readingText(reading),
    })),
  );

  const lines: PdfLine[] = [
    { text: "Bite Balance", size: 18, color: "white", bold: true },
    { text: "Weekly health report", size: 12, color: "white" },
    { text: formatRange(start, end), size: 10, color: "white" },
    { text: "", size: 10 },
    {
      text: "Summary for a regular check-up. Share this with your clinician. It records what the app logged. It is not a diagnosis.",
      size: 10,
      color: "muted",
    },
    { text: "Score trend", size: 13, color: "ink", bold: true, section: true },
    {
      text: avgScore == null ? "No Bite Balance Score was logged on these days." : `Average Bite Balance Score this week: ${avgScore}.`,
      size: 10,
      color: "ink",
    },
    ...days.map((day) => ({
      text: `${day.date}    ${typeof day.bite_balance_score === "number" ? Math.round(day.bite_balance_score) : "-"}${day.bite_balance_label ? ` (${day.bite_balance_label})` : ""}`,
      size: 10,
      color: "muted" as const,
    })),
    { text: "Meals", size: 13, color: "ink", bold: true, section: true },
  ];

  if (meals.length === 0) {
    lines.push({ text: "No meals were logged this week.", size: 10, color: "muted" });
  } else {
    meals.forEach(({ day, meal }) => {
      const spike = typeof meal.spike_mg_dl === "number" ? `  spike +${Math.round(meal.spike_mg_dl)} mg/dL` : "";
      const name = meal.meal_name?.trim() || meal.meal_type || "Meal";
      lines.push({
        text: `${day.date}  ${meal.meal_type ? `${meal.meal_type} · ` : ""}${name}${spike}`,
        size: 10,
        color: "ink",
        bold: true,
      });
      const foods = foodLabel(meal);
      if (foods && foods !== name) {
        lines.push({ text: foods, size: 10, color: "muted" });
      }
    });
  }

  lines.push({ text: "Foods linked to higher glucose spikes", size: 13, color: "ink", bold: true, section: true });
  if (highSpikes.length === 0) {
    lines.push({
      text: "No meal in this week was marked as a high spike (80 mg/dL or more, or a High category).",
      size: 10,
      color: "muted",
    });
  } else {
    highSpikes.forEach(({ day, meal }) => {
      const amount = typeof meal.spike_mg_dl === "number" ? `+${Math.round(meal.spike_mg_dl)} mg/dL` : meal.spike_category || "High";
      lines.push({ text: `${day.date}  ${foodLabel(meal)}  (${amount})`, size: 10, color: "ink" });
    });
  }

  lines.push({ text: "Activity", size: 13, color: "ink", bold: true, section: true });
  lines.push({ text: `Average steps: ${avgSteps.toLocaleString("en-US")} per day.`, size: 10, color: "ink" });
  days.forEach((day) => {
    lines.push({
      text: `${day.date}    Steps ${day.steps || 0}    Calories ${Math.round(day.calories_burned || 0)}    Sleep ${day.sleep || "-"}`,
      size: 10,
      color: "muted",
    });
  });

  lines.push({ text: "Blood sugar readings entered", size: 13, color: "ink", bold: true, section: true });
  if (readings.length === 0) {
    lines.push({
      text: "No finger-prick, fasting, CGM, or HbA1c reading was entered for these days.",
      size: 10,
      color: "muted",
    });
  } else {
    readings.forEach((reading) => {
      lines.push({ text: `${reading.date}    ${reading.text}`, size: 10, color: "muted" });
    });
  }

  lines.push({ text: "Note for your clinician", size: 13, color: "ink", bold: true, section: true });
  lines.push({ text: MEDICAL_DISCLAIMER_TEXT, size: 9, color: "muted" });
  return lines;
}

export async function downloadWeeklyReport(endDate: string): Promise<void> {
  const start = shiftLocalDate(endDate, -6);
  const report = await trendsService.getWeeklyReport(start, endDate);
  const days = report?.days ?? [];
  if (!report || days.length === 0) {
    throw new Error("Weekly report was empty");
  }
  const rangeStart = report.start_date || start;
  const rangeEnd = report.end_date || endDate;
  savePdf(
    `bite-balance-weekly-report-${rangeStart}-to-${rangeEnd}.pdf`,
    reportLines(days, rangeStart, rangeEnd),
  );
}

function pdfEscape(value: string): string {
  return value.replace(/[^\x20-\x7E]/g, " ").replace(/\\/g, "\\\\").replace(/\(/g, "\\(").replace(/\)/g, "\\)");
}

function wrapPdfText(text: string, size: number): string[] {
  const width = size >= 16 ? 42 : size >= 13 ? 62 : 88;
  const words = pdfEscape(text).split(/\s+/).filter(Boolean);
  if (words.length === 0) return [""];
  const lines: string[] = [];
  let current = "";
  words.forEach((word) => {
    const next = current ? `${current} ${word}` : word;
    if (next.length > width && current) {
      lines.push(current);
      current = word;
    } else {
      current = next;
    }
  });
  if (current) lines.push(current);
  return lines;
}

function savePdf(filename: string, source: PdfLine[]): void {
  const pageWidth = 595;
  const pageHeight = 842;
  const margin = 40;
  const pages: string[] = [];
  let commands: string[] = [];
  let y = pageHeight - 36;

  const flush = () => {
    pages.push(commands.join("\n"));
    commands = [];
    y = pageHeight - 48;
  };

  const paintHeader = () => {
    commands.push(`${BRAND.r / 255} ${BRAND.g / 255} ${BRAND.b / 255} rg`);
    commands.push(`0 ${pageHeight - 78} ${pageWidth} 78 re f`);
  };

  paintHeader();

  source.forEach((line) => {
    const wrapped = wrapPdfText(line.text, line.size);
    const gap = line.size + 4;
    const block = Math.max(gap, wrapped.length * gap) + (line.section ? 10 : 0);
    if (y - block < 48) flush();
    if (line.section) {
      y -= 8;
      commands.push(`${BRAND.r / 255} ${BRAND.g / 255} ${BRAND.b / 255} rg`);
      commands.push(`${margin} ${y - 2} 3 12 re f`);
    }
    const color = line.color === "white" ? "1 1 1" : line.color === "muted" ? "0.31 0.31 0.33" : "0.09 0.09 0.11";
    const font = line.bold ? "F2" : "F1";
    wrapped.forEach((part) => {
      commands.push("BT");
      commands.push(`/${font} ${line.size} Tf`);
      commands.push(`${color} rg`);
      commands.push(`1 0 0 1 ${margin + (line.section ? 10 : 0)} ${y} Tm`);
      commands.push(`(${part}) Tj`);
      commands.push("ET");
      y -= gap;
    });
  });
  if (commands.length) pages.push(commands.join("\n"));

  const objects: string[] = [];
  objects[1] = "<< /Type /Catalog /Pages 2 0 R >>";
  objects[3] = "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>";
  objects[4] = "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>";

  const pageIds: number[] = [];
  let nextId = 5;
  pages.forEach((stream, index) => {
    const footer = `BT /F1 9 Tf 0.31 0.31 0.33 rg 1 0 0 1 ${margin} 28 Tm (Bite Balance weekly report  -  Page ${index + 1} of ${pages.length}) Tj ET`;
    const body = `${stream}\n${footer}`;
    const contentId = nextId;
    const pageId = nextId + 1;
    nextId += 2;
    pageIds.push(pageId);
    objects[contentId] = `<< /Length ${body.length} >>\nstream\n${body}\nendstream`;
    objects[pageId] =
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageWidth} ${pageHeight}] /Contents ${contentId} 0 R /Resources << /Font << /F1 3 0 R /F2 4 0 R >> >> >>`;
  });

  objects[2] = `<< /Type /Pages /Count ${pages.length} /Kids [${pageIds.map((id) => `${id} 0 R`).join(" ")}] >>`;

  let pdf = "%PDF-1.4\n";
  const offsets = [0];
  for (let id = 1; id < objects.length; id += 1) {
    if (!objects[id]) continue;
    offsets[id] = pdf.length;
    pdf += `${id} 0 obj\n${objects[id]}\nendobj\n`;
  }
  const xref = pdf.length;
  const maxId = objects.length - 1;
  pdf += `xref\n0 ${maxId + 1}\n`;
  pdf += "0000000000 65535 f \n";
  for (let id = 1; id <= maxId; id += 1) {
    pdf += `${String(offsets[id] || 0).padStart(10, "0")} 00000 n \n`;
  }
  pdf += `trailer\n<< /Size ${maxId + 1} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF`;

  const blob = new Blob([pdf], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}
